<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NButton, NInput, NSelect, NCard, NBadge, NTag, NDropdown } from 'naive-ui'
import {
  LANGUAGES, getCurrentLanguage, setCurrentLanguage,
  getLocaleFromPreferred, getLanguageLabel
} from '@/services/language'
import doctorImage from '@/assets/images/alejandro-pohlenz-gYbOFTwcJx4-unsplash.jpg'
import mobileAppImage from '@/assets/images/william-hook-9e9PD9blAto-unsplash.jpg'
import appStoreImage from '@/assets/images/laurenz-heymann-VkfhJLz5SMQ-unsplash.jpg'
import playStoreImage from '@/assets/images/appshunter-io-1k3LBBp6_ns-unsplash.jpg'
import { dt } from '@/utils/i18n'

const router = useRouter()
const { t, locale } = useI18n()
const showModal = ref(false)
const searchQuery = ref("")
const selectedLanguage = ref(getCurrentLanguage())

// Update locale when component mounts
onMounted(() => {
  // Set the i18n locale based on user's preferred language
  locale.value = getLocaleFromPreferred(getCurrentLanguage())
})

const handleLanguageChange = (value) => {
  selectedLanguage.value = value
  setCurrentLanguage(value)
  locale.value = getLocaleFromPreferred(value)
}

const onProviderSaved = (savedProvider) => {
  router.push({
    name: 'provider-detail',
    params: { id: savedProvider.id }
  })
}

const handleSearch = () => {

  // Create query parameters
  const params = new URLSearchParams()
  if (searchQuery.value) params.append('q', searchQuery.value)

  // Use the current selected language
  params.append('language', selectedLanguage.value)

  // Navigate to providers page with search parameters
  router.push({
    path: '/search',
    query: Object.fromEntries(params)
  })
}

// Language dropdown options
const languageOptions = LANGUAGES.map(lang => ({
  label: lang.label,
  key: lang.value
}))

// Function to handle dropdown selection
const handleLanguageSelect = (key) => {
  handleLanguageChange(key)
}
</script>

<template>
  <div class="homepage">
    <!-- Hero Section with Image Right, Text Left -->
    <section class="hero">
      <div class="container">
        <div class="hero-grid">
          <div class="hero-content">
            <h1 class="hero-title">{{ dt('home.heroTitle') }}</h1>
            <p class="hero-subtitle">{{ dt('home.heroSubtitle') }}</p>

            <div class="search-section">
              <div class="search-container">
                <NInput v-model:value="searchQuery" :placeholder="dt('home.searchPlaceholder')" class="search-input"
                  size="large" />
                <NButton type="primary" size="large" class="search-button" @click="handleSearch">
                  🔍 {{ dt('common.search') }}
                </NButton>
              </div>
            </div>
          </div>

          <div class="hero-image-section">
            <img :src="doctorImage" alt="Legal Professional" class="hero-image" />
          </div>
        </div>
      </div>
    </section>

    <!-- Language Coverage -->
    <section class="languages">
      <div class="container">
        <h3 class="section-title">{{ dt('home.languagesTitle') }}</h3>
        <div class="language-tags">
          <NTag v-for="lang in LANGUAGES" :key="lang.value" type="info" size="large" class="lang-tag">
            {{ lang.label }}
          </NTag>
        </div>
      </div>
    </section>

    <!-- How It Works - Horizontal Timeline -->
    <section class="how-it-works">
      <div class="container">
        <h3 class="section-title">{{ dt('home.howItWorksTitle') }}</h3>
        <div class="timeline">
          <div class="timeline-item">
            <div class="timeline-marker">1</div>
            <div class="timeline-content">
              <h4>{{ dt('home.steps.chooseLanguage.title') }}</h4>
              <p>{{ dt('home.steps.chooseLanguage.description') }}</p>
            </div>
          </div>
          <div class="timeline-connector"></div>
          <div class="timeline-item">
            <div class="timeline-marker">2</div>
            <div class="timeline-content">
              <h4>{{ dt('home.steps.searchProviders.title') }}</h4>
              <p>{{ dt('home.steps.searchProviders.description') }}</p>
            </div>
          </div>
          <div class="timeline-connector"></div>
          <div class="timeline-item">
            <div class="timeline-marker">3</div>
            <div class="timeline-content">
              <h4>{{ dt('home.steps.connect.title') }}</h4>
              <p>{{ dt('home.steps.connect.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Features - Card Layout -->
    <section class="features">
      <div class="container">
        <h3 class="section-title">{{ dt('home.featuresTitle') }}</h3>
        <div class="feature-cards">
          <NCard class="feature-card">
            <div class="feature-content">
              <div class="feature-icon">🗣️</div>
              <div class="feature-text">
                <h4>{{ dt('home.features.multilingual.title') }}</h4>
                <p>{{ dt('home.features.multilingual.description') }}</p>
              </div>
            </div>
          </NCard>
          <NCard class="feature-card">
            <div class="feature-content">
              <div class="feature-icon">📍</div>
              <div class="feature-text">
                <h4>{{ dt('home.features.location.title') }}</h4>
                <p>{{ dt('home.features.location.description') }}</p>
              </div>
            </div>
          </NCard>
          <NCard class="feature-card">
            <div class="feature-content">
              <div class="feature-icon">👥</div>
              <div class="feature-text">
                <h4>{{ dt('home.features.specialty.title') }}</h4>
                <p>{{ dt('home.features.specialty.description') }}</p>
              </div>
            </div>
          </NCard>
          <NCard class="feature-card">
            <div class="feature-content">
              <div class="feature-icon">💬</div>
              <div class="feature-text">
                <h4>{{ dt('home.features.questions.title') }}</h4>
                <p>{{ dt('home.features.questions.description') }}</p>
              </div>
            </div>
          </NCard>
          <NCard class="feature-card">
            <div class="feature-content">
              <div class="feature-icon">⭐</div>
              <div class="feature-text">
                <h4>{{ dt('home.features.verified.title') }}</h4>
                <p>{{ dt('home.features.verified.description') }}</p>
              </div>
            </div>
          </NCard>
          <NCard class="feature-card">
            <div class="feature-content">
              <div class="feature-icon">🔒</div>
              <div class="feature-text">
                <h4>{{ dt('home.features.secure.title') }}</h4>
                <p>{{ dt('home.features.secure.description') }}</p>
              </div>
            </div>
          </NCard>
        </div>
      </div>
    </section>

    <!-- Mobile App Section -->
    <section class="mobile-app">
      <div class="container">
        <div class="app-content">
          <div class="app-text">
            <h3 class="app-title">{{ dt('home.mobileApp.title') }}</h3>
            <p class="app-description">{{ dt('home.mobileApp.description') }}</p>
            <div class="app-stores">
              <a href="#" class="app-store-link">
                <img :src="appStoreImage" alt="App Store" class="store-icon" />
              </a>
              <a href="#" class="app-store-link">
                <img :src="playStoreImage" alt="Play Store" class="store-icon" />
              </a>
            </div>
          </div>
          <div class="app-image-container">
            <img :src="mobileAppImage" alt="Mobile App" class="mobile-app-image" />
          </div>
        </div>
      </div>
    </section>

    <!-- Community Section -->
    <section class="community">
      <div class="container">
        <h3 class="section-title">{{ dt('home.communityTitle') }}</h3>
        <div class="community-grid">
          <div class="community-card">
            <div class="community-icon">⭐</div>
            <h4>{{ dt('home.community.reviews.title') }}</h4>
            <p>{{ dt('home.community.reviews.description') }}</p>
          </div>
          <div class="community-card">
            <div class="community-icon">🔖</div>
            <h4>{{ dt('home.community.bookmark.title') }}</h4>
            <p>{{ dt('home.community.bookmark.description') }}</p>
          </div>
          <div class="community-card">
            <div class="community-icon">❓</div>
            <h4>{{ dt('home.community.qa.title') }}</h4>
            <p>{{ dt('home.community.qa.description') }}</p>
          </div>
          <div class="community-card">
            <div class="community-icon">🤝</div>
            <h4>{{ dt('home.community.network.title') }}</h4>
            <p>{{ dt('home.community.network.description') }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.homepage {
  min-height: 100vh;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: #ffffff;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section - Clean White with Image Right */
.hero {
  background: white;
  padding: 4rem 0;
  min-height: 20vh;
  display: flex;
  align-items: center;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.hero-content {
  text-align: left;
}

.hero-title {
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  font-weight: 800;
  color: #1a202c;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  color: #4a5568;
  line-height: 1.6;
  font-weight: 400;
}

.hero-image-section {
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-image {
  width: 100%;
  max-width: 500px;
  height: 400px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
}

.search-section {
  margin-top: 2rem;
}

.search-container {
  display: flex;
  gap: 1rem;
  max-width: 500px;
}

.search-input {
  flex-grow: 1;
}

.search-button {
  background: #2d3748;
  border: none;
  font-weight: 600;
}

.search-button:hover {
  background: #1a202c;
}

/* Languages Section */
.languages {
  background: #f7fafc;
  padding: 4rem 0;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #1a202c;
  font-weight: 700;
}

.language-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  max-width: 800px;
  margin: 0 auto;
}

.lang-tag {
  font-size: 1.1rem;
  padding: 0.5rem 1rem;
}

/* How It Works - Timeline */
.how-it-works {
  padding: 5rem 0;
  background: white;
}

.timeline {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
}

.timeline-item {
  text-align: center;
  flex: 1;
  position: relative;
  z-index: 2;
}

.timeline-marker {
  width: 60px;
  height: 60px;
  background: #2d3748;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 1.5rem;
  font-weight: bold;
  border: 4px solid white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.timeline-content {
  background: white;
  padding: 2rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  margin: 0 1rem;
  border: 1px solid #e2e8f0;
}

.timeline-content h4 {
  color: #1a202c;
  margin-bottom: 1rem;
  font-weight: 600;
}

.timeline-content p {
  color: #4a5568;
  margin: 0;
  line-height: 1.6;
}

.timeline-connector {
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  height: 4px;
  background: #2d3748;
  z-index: 1;
}

/* Features Section */
.features {
  padding: 5rem 0;
  background: #f7fafc;
}

.feature-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.feature-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
}

.feature-content {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1rem;
}

.feature-icon {
  font-size: 2.5rem;
  flex-shrink: 0;
}

.feature-text h4 {
  margin-bottom: 0.75rem;
  color: #1a202c;
  font-weight: 600;
}

.feature-text p {
  color: #4a5568;
  line-height: 1.6;
  margin: 0;
}

/* Mobile App Section */
.mobile-app {
  background: #1a202c;
  color: white;
  padding: 5rem 0;
}

.app-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 4rem;
  align-items: center;
}

.app-title {
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  color: white;
}

.app-description {
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  opacity: 0.9;
  color: #e2e8f0;
}

.app-stores {
  display: flex;
  gap: 1.5rem;
}

.app-store-link {
  transition: transform 0.3s ease;
  display: block;
}

.app-store-link:hover {
  transform: translateY(-5px);
}

.store-icon {
  height: 50px;
  width: auto;
  border-radius: 8px;
}

.app-image-container {
  text-align: center;
}

.mobile-app-image {
  max-width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

/* Community Section */
.community {
  padding: 5rem 0;
  background: white;
}

.community-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 900px;
  margin: 0 auto;
}

.community-card {
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.3s ease;
  border: 1px solid #e2e8f0;
}

.community-card:hover {
  transform: translateY(-5px);
}

.community-badge {
  margin-bottom: 1rem;
}

.community-card h4 {
  color: #1a202c;
  margin-bottom: 1rem;
  font-weight: 600;
}

.community-card p {
  color: #4a5568;
  line-height: 1.6;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .hero-content {
    order: 1;
    text-align: center;
  }

  .hero-image-section {
    order: 0;
    margin-bottom: 2rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .hero-image {
    height: 250px;
  }

  .search-container {
    flex-direction: column;
    gap: 0.5rem;
  }

  .timeline {
    flex-direction: column;
    gap: 2rem;
  }

  .timeline-connector {
    display: none;
  }

  .timeline-content {
    margin: 0;
  }

  .feature-content {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .app-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }

  .app-image-container {
    order: -1;
  }

  .mobile-app-image {
    height: 200px;
  }

  .community-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .timeline-marker {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .hero-image {
    height: 200px;
  }
}
</style>